import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { rememberMarketingConsent } from './usePendingMarketingConsent'
import { useInstitutionStore } from '@entities/institution'

/**
 * Согласия на форме входа/регистрации и проверка перед любым способом авторизации.
 *
 * Почему проверка в обработчике, а не `disabled`/`pointer-events` на кнопках:
 *  - кнопки соцсетей приходят из разных источников (VK ID рисует свой виджет сам),
 *    и точечные `disabled` разъезжались бы при добавлении нового способа входа;
 *  - CSS-гейт `pointer-events: none` обходится с клавиатуры: Tab до кнопки и Enter
 *    запускали OAuth мимо галочки — то есть согласие не собиралось вовсе;
 *  - серая заблокированная кнопка не объясняет, почему она не работает.
 * Поэтому кнопки выглядят обычными, а по нажатию без согласия подсвечивается сама
 * галочка — человек видит, чего от него хотят.
 *
 * ⚠️ Обязательность галочки рекламы — настройка заведения
 * (`marketing_consent_required`, переключатель в админке, раздел «Основные»).
 * Юридическая оговорка, ради которой её и вывели в интерфейс: согласие,
 * полученное под условием «иначе не пустим», не является свободным по ст. 9 ч. 1
 * 152-ФЗ, и при жалобе такие согласия могут признать недействительными — то есть
 * защиты по ст. 18 ФЗ «О рекламе» они не дадут. Владелец может снять требование
 * в любой момент, без выката.
 *
 * Пока флаг не доехал (первая загрузка, оффлайн) считаем галочку обязательной —
 * совпадает с дефолтом колонки в БД, поведение не «моргает».
 */
export interface AuthConsentGate {
  /** Согласие с политикой и пользовательским соглашением. Обязательное. */
  isPolicyAccepted: Ref<boolean>
  /** Согласие на рекламные рассылки. Обязательность — настройка заведения. */
  isMarketingAccepted: Ref<boolean>
  /** Была попытка входа без согласия: подсвечиваем непоставленные галочки. */
  showConsentError: Ref<boolean>
  /**
   * Обе галочки на месте. Нужен шаблонам: кнопки сторонних SDK (VK OneTap,
   * Яндекс) рисуются чужим кодом, и их клик перехватывает слой поверх — он
   * висит ровно пока согласия неполны.
   */
  isConsentComplete: ComputedRef<boolean>
  /** Требует ли заведение галочку рекламы для входа. */
  isMarketingRequired: ComputedRef<boolean>
  /** Ссылка на блок согласий: к нему прокручиваем, если он ушёл из виду. */
  consentBlock: Ref<HTMLElement | null>
  /**
   * Проверка перед любым способом входа. `false` — согласия нет, вход не начинаем.
   * При успехе фиксирует выбор по рекламе: до этого момента человек мог передумать
   * и закрыть окно, и записывать его решение было бы не за что.
   */
  requireConsent: () => boolean
}

export const useAuthConsentGate = (): AuthConsentGate => {
  const isPolicyAccepted = ref(false)
  const isMarketingAccepted = ref(false)
  const showConsentError = ref(false)
  const consentBlock = ref<HTMLElement | null>(null)

  const institutionStore = useInstitutionStore()
  // Дефолт true: совпадает с колонкой в БД, поэтому до приезда настройки
  // требование не «моргает» с необязательного на обязательное.
  const isMarketingRequired = computed(
    () => institutionStore.institution?.marketing_consent_required !== false,
  )

  const isConsentComplete = computed(
    () => isPolicyAccepted.value && (!isMarketingRequired.value || isMarketingAccepted.value),
  )

  // Все нужные галочки на месте — подсветка больше не нужна.
  watch(isConsentComplete, (complete) => {
    if (complete) showConsentError.value = false
  })

  const requireConsent = (): boolean => {
    if (!isConsentComplete.value) {
      showConsentError.value = true
      // Блок согласий стоит внизу, а соцкнопки — выше: без прокрутки человек
      // увидел бы только то, что нажатие ничего не сделало.
      consentBlock.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return false
    }

    rememberMarketingConsent(isMarketingAccepted.value)
    return true
  }

  return {
    isPolicyAccepted,
    isMarketingAccepted,
    showConsentError,
    isConsentComplete,
    isMarketingRequired,
    consentBlock,
    requireConsent,
  }
}
